import { useEffect, useId, useMemo, useRef, useState } from "react";

import { FieldNotch } from "@/components/ui/field-notch";
import { IconButton } from "@/components/ui/icon-button";
import {
  MUI_FIELD_HELPER_TEXT,
  MUI_FIELD_LABEL,
  MUI_FIELD_LABEL_FOCUSED,
  MUI_FIELD_LABEL_PRESS_STAND_DOWN,
  MUI_FIELD_LABEL_RESTING,
  MUI_FIELD_LABEL_RESTING_SMALL,
  MUI_FIELD_LABEL_TRANSITION,
  MUI_FIELD_NOTCHED_TRIGGER,
  MUI_FIELD_NOTCHED_TRIGGER_MEDIUM,
  MUI_FIELD_OUTLINE_PEER,
  MUI_FIELD_TRIGGER,
  MUI_MENU_ITEM,
  MUI_MENU_LIST,
  MUI_SELECT_MENU_PANEL,
} from "@/components/ui/mui-field-shape";
import { MenuPopoverContent } from "@/components/ui/menu-select";
import { MuiCollapse } from "@/components/ui/mui-collapse";
import { RippleButton } from "@/components/ui/ripple-button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { MuiArrowDropDownIcon, MuiArrowLeftIcon } from "@/components/ui/mui-arrow-icons";
import {
  MUI_SELECT_CARET_ANCHOR,
  MUI_SELECT_CARET_DISABLED,
  MUI_SELECT_CARET_GUTTER,
  MuiSelectCaret,
} from "@/components/ui/mui-select-caret";
import { cn } from "@/lib/utils";
import { RequiredAsterisk } from "./required-asterisk";

// A single-select over a tree, with a search box in the menu and branches the
// user expands — the donor's `CategoriesFieldPaginated`
// (`timmy/src/components/assets/CategoriesFieldPaginated.tsx`), ported as a
// shared Atlas component because it is shared in the donor: the items list's
// category filter draws it, and the item edit form and the search island draw
// the same control.
//
// The visual facts, read off the donor and mapped one by one:
//
//   - the trigger is MUI's outlined `size='small'` select (`mui-field-shape`)
//     with the field label shrunk onto the outline — the donor passes
//     `allowEmpty`, which pins `shrink` on, so the label never sits inside,
//   - empty renders the caller's own word for "no filter" ("All Categories"),
//     which is also the menu's first row, in 16px weight-500 above a divider,
//   - the menu is capped at 500px and scrolls, with a sticky, auto-focused
//     search field at the top,
//   - a row is indented `16 + depth * 16` pixels,
//   - a top-level row is weight 500; deeper, a row with children is 500 and a
//     leaf is 400,
//   - a row with children carries a toggle on its right — pointing down when
//     open and left when closed — which expands rather than selects,
//   - the current row is tinted, MUI's mark for "selected".
//
// **The donor paginates; this does not, and the control is the same one.** Its
// `getQuery` fetches 100 categories at a time and appends on scroll because
// Falcon pages that read; Atlas's tree read answers with the whole alive tree
// in one response (`/internal/catalog/categories/tree`), so there is nothing
// left to fetch as the user scrolls. What the user does — type to narrow, open
// a branch, scroll — is unchanged.
//
// Filtering is the caller's: it owns the tree's shape, and "what counts as a
// match" differs per tree (a category match keeps its whole subtree). This
// component owns the *control*.

export interface TreeSelectNode {
  id: number;
  name: string;
  /** Depth in the tree, used to indent. 0 is a root. */
  depth: number;
  children: TreeSelectNode[];
}

interface TreeSelectProps {
  /** The field label, drawn on the outline. Also the trigger's accessible name. */
  label: string;
  value: number | null;
  /** The tree to draw, already narrowed by `searchTerm` if the caller narrows. */
  nodes: readonly TreeSelectNode[];
  onChange: (next: number | null) => void;
  /** What the trigger and the first row read when nothing is selected. */
  emptyLabel: string;
  /**
   * What the trigger reads when something *is* selected but this tree cannot
   * name it — the caller's own words for the unresolved selection. Without it
   * the trigger falls back to `#<id>`; with it, a caller that addresses rows by
   * something other than the id (a slug in a URL) can say what was asked for.
   */
  unresolvedLabel?: string;
  searchPlaceholder: string;
  /** The term the caller is filtering by, and how to change it. */
  searchTerm: string;
  onSearchTermChange: (next: string) => void;
  /** The ids to open on, so a selected row deep in the tree is on screen. */
  expandedByDefault?: readonly number[];
  loading?: boolean;
  /** The donor's `FormControl disabled` — a form field is inert while its form
   * is submitting. */
  disabled?: boolean;
  /**
   * Whether "no selection" is one of the choices.
   *
   * The donor's own prop, and it decides two things at once
   * (`CategoriesFieldPaginated.tsx:34,260,302`): the empty first row and the
   * divider under it, and whether the label is *forced* shrunk. A filter allows
   * empty and so always shows a value, which is why its label sits on the
   * outline from the start; a required form field does not, so its label rests
   * inside the field until something is picked.
   */
  allowEmpty?: boolean;
  /**
   * MUI's field size. The donor's list toolbar is `small`; a form field takes
   * MUI's default, which is `medium` — 56px, matching the fields beside it.
   */
  size?: "small" | "medium";
  /** Marks the label, the way every required field in the ported forms does. */
  required?: boolean;
  /** The field's own error, drawn as MUI's error outline plus helper text. */
  error?: string;
  className?: string;
  "data-testid"?: string;
}

/** Every node in the tree, flat — for resolving the selected row's name. */
function flatten(nodes: readonly TreeSelectNode[]): TreeSelectNode[] {
  return nodes.flatMap((node) => [node, ...flatten(node.children)]);
}

/**
 * One `MenuItem`'s worth of button.
 *
 * `tabIndex={-1}` on every one of them: the listbox owns the focus and moves a
 * cursor through the rows, so a row that could be tabbed to would be a second
 * way to reach what the arrow keys already reach.
 *
 * No `focusRipple` either — `MenuItem.js` never sets it, so the donor's rows
 * ink under the pointer and stay silent under Space. Doubly moot here, since a
 * `tabIndex={-1}` row never holds the focus a Space press would need.
 */
/**
 * The four states a menu row can be in, as one tint.
 *
 * MUI **adds** the interaction tint to the selected one rather than replacing
 * it: `.selected:hover` is `alpha(primary.main, selectedOpacity +
 * hoverOpacity)` = 0.12 and `.selected.focusVisible` is `selectedOpacity +
 * focusOpacity` = 0.2 (`MenuItem.js:84-95`). Both composites are tokens
 * (`index.css:298-299`), and `multi-select-field.tsx:472-478` spells the same
 * matrix out.
 *
 * It is one function rather than entries in each row's `cn()` because the
 * states collide when they are written separately: a `hover:` variant outranks
 * a plain selected utility, and tailwind-merge drops a selected background that
 * a later `bg-*` supersedes. Either way the selected row loses its mark under
 * the pointer. Returning whole literals also keeps Tailwind v4's source-text
 * scanner satisfied — an interpolated prefix would generate no CSS.
 *
 * Hover beats the walk on a selected row because MUI declares `.selected:hover`
 * **after** `.selected.focusVisible` and the two tie on specificity.
 */
function rowTint(chosen: boolean, active: boolean) {
  if (chosen) {
    return active
      ? "bg-lennar-primary-selected-focus hover:bg-lennar-primary-selected-hover"
      : "bg-lennar-primary-selected hover:bg-lennar-primary-selected-hover";
  }
  return active
    ? "bg-lennar-action-focus hover:bg-lennar-action-focus"
    : "hover:bg-lennar-action-hover";
}

function MenuRowButton({ className, ...props }: React.ComponentProps<typeof RippleButton>) {
  return (
    <RippleButton
      tabIndex={-1}
      // **The row owns the hover tint, not the label.** In the donor the row
      // *is* the `MenuItem`, so `&:hover` paints all of it — hovering the
      // expander lights the label too (`MenuItem.js:76-82`). Here the label is
      // only part of a branch row, so `MUI_MENU_ITEM`'s own hover is given up
      // and the container's takes over; left on, the two 0.04 tints would
      // composite to 0.078 over the label and band the row.
      className={cn(MUI_MENU_ITEM, "hover:bg-transparent", className)}
      {...props}
    />
  );
}

export function TreeSelect({
  label,
  value,
  nodes,
  onChange,
  emptyLabel,
  unresolvedLabel,
  searchPlaceholder,
  searchTerm,
  onSearchTermChange,
  expandedByDefault = [],
  loading = false,
  disabled = false,
  allowEmpty = true,
  size = "small",
  required = false,
  error,
  className,
  "data-testid": testId,
}: TreeSelectProps) {
  const [open, setOpen] = useState(false);
  // **The trigger's own focus, which is not the same thing as the menu.** MUI
  // keeps a `FormControl` `focused` for as long as its `Select` holds focus
  // *or* its menu is open: `SelectInput` swallows the blur while open
  // (`SelectInput.js:549-561`), and the focus comes straight back to the
  // trigger when the menu closes. Measured on the running donor's item edit
  // page: tab-focusing an empty `Material` select, with no menu, floats its
  // label, opens its notch and takes its outline to 2px navy.
  const [triggerFocused, setTriggerFocused] = useState(false);
  const focused = triggerFocused || open;
  const messageId = useId();
  // What the *user* has toggled, id by id — not which branches are open. Which
  // branches are open is derived below, because the defaults arrive later than
  // the first render (the tree is a read) and a state seeded from them would
  // have to be re-seeded from an effect when they land. A map of overrides has
  // no such catching-up to do: a branch the user has not touched simply reads
  // the current defaults, whenever they show up.
  const [toggled, setToggled] = useState<ReadonlyMap<number, boolean>>(new Map());

  const openByDefault = useMemo(() => new Set(expandedByDefault), [expandedByDefault]);

  // Which row the keyboard is on, as an index into `visibleRows` below. `-1` is
  // "none yet" — MUI's own starting point, where Enter does nothing rather than
  // taking whatever sorts first.
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  // Whether a term is typed. It shrinks the label and it is what `close()`
  // clears — it deliberately does **not** open branches. The donor's search is
  // a filter and nothing more (`CategoriesFieldPaginated.tsx:47-52,224-226`,
  // which never touches `expandedItems`); forcing every branch open made a
  // search that returned `Appliances` render ten unmatched leaves under it,
  // with the expanders switched off so none of it could be folded away (#634).
  const searching = searchTerm.trim() !== "";

  // MUI's rule, verbatim: `shrink={!!field.value || !!inputValue || isFetching
  // || allowEmpty}` (`CategoriesFieldPaginated.tsx:260`). A filter is always
  // shrunk because it always reads a value.
  //
  // **Opening the menu is not on that list**, and an *explicit* `shrink`
  // outranks MUI's own `filled || focused` computation (`InputLabel.js`), so an
  // empty required Category keeps its label inside the box even with the menu
  // over it. Adding `|| open` here rode the label up onto the outline on every
  // open — visible on this form for an item with no category, and on the
  // Request Item dialog's Category field as the normal case.
  const shrunk = allowEmpty || value !== null || searching || loading;

  const selectedName = useMemo(
    () => flatten(nodes).find((node) => node.id === value)?.name,
    [nodes, value],
  );

  function close() {
    setOpen(false);
    setActiveIndex(-1);
    // The donor clears the typed term when the menu closes, so re-opening
    // starts from the whole tree rather than from the last search.
    onSearchTermChange("");
  }

  function pick(next: number | null) {
    onChange(next);
    close();
  }

  const isExpanded = (id: number) => toggled.get(id) ?? openByDefault.has(id);

  function toggle(id: number) {
    setToggled((current) => new Map(current).set(id, !isExpanded(id)));
  }

  // Every row the user can actually see, in the order they are drawn: the
  // empty row, then the tree with each folded branch's children left out. It is
  // what the arrow keys walk, so a hidden row can never be highlighted.
  const visibleRows: { id: number | null; node?: TreeSelectNode }[] = allowEmpty
    ? [{ id: null }]
    : [];
  const collectVisible = (node: TreeSelectNode) => {
    visibleRows.push({ id: node.id, node });
    if (isExpanded(node.id)) node.children.forEach(collectVisible);
  };
  if (!loading) nodes.forEach(collectVisible);

  // The highlighted row is scrolled into view: the menu caps at 500px and the
  // tree runs to hundreds of rows, so a walk that leaves the viewport behind is
  // a walk into nowhere.
  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-row-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function onMenuKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (visibleRows.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        if (current === -1) return step === 1 ? 0 : visibleRows.length - 1;
        return (current + step + visibleRows.length) % visibleRows.length;
      });
      return;
    }
    const active = activeIndex >= 0 ? visibleRows[activeIndex] : undefined;
    // Right opens the branch under the cursor, left folds it — the tree
    // convention, and the only way to reach a nested category without a mouse.
    if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && active?.node) {
      if (active.node.children.length === 0) return;
      event.preventDefault();
      if (isExpanded(active.node.id) === (event.key === "ArrowRight")) return;
      toggle(active.node.id);
      return;
    }
    if (event.key === "Enter" && active) {
      event.preventDefault();
      pick(active.id);
    }
  }

  let rowIndex = allowEmpty ? 0 : -1;

  // `walkable` is false for the rows of a branch that is *closing*: they are
  // still in the DOM while the `Collapse` runs, but `visibleRows` — which the
  // arrow keys index into — was built from `isExpanded` and has already
  // dropped them. Letting them take a number here would shift every index
  // below them for the length of the animation.
  const rows = (node: TreeSelectNode, walkable = true): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const isOpen = isExpanded(node.id);
    const index = walkable ? ++rowIndex : -1;
    const chosen = value === node.id;
    const active = index >= 0 && index === activeIndex;

    return (
      <li key={node.id}>
        <div
          role="option"
          id={index >= 0 ? `${listId}-${index}` : undefined}
          data-row-index={index >= 0 ? index : undefined}
          aria-selected={value === node.id}
          // **The row selects, not the label inside it.** The donor's row
          // *is* the `MenuItem` and the handler is on it
          // (`CategoriesFieldPaginated.tsx:144-153`), so its `padding: 6px
          // 16px` picks the category exactly like the text does. Hanging this
          // on the label button instead left the 6px bands and the 16px right
          // inset tinting under the pointer and swallowing the press.
          onClick={() => pick(node.id)}
          className={cn(
            "flex items-center",
            // **The branch row's height is the donor's `MenuItem` padding, not
            // its toggle's box.** The donor wraps a 40px `IconButton` in a
            // `MenuItem` whose `padding: 6px 16px` it keeps
            // (`CategoriesFieldPaginated.tsx:163-173`), so a branch stands 52
            // and the toggle sits 16 in from the row's edge. Atlas let the
            // toggle *be* the row and bought the inset with `mr-2`: 40 and 8.
            // Only branches take it — a leaf has no toggle, and its label's own
            // `MUI_MENU_ITEM` is already the donor's 36.
            hasChildren && "py-1.5 pr-4",
            rowTint(chosen, active),
          )}
        >
          <MenuRowButton
            // No handler: the click belongs to the row above, the way the
            // donor's `ListItemText` carries none and its `MenuItem` does.
            className={cn(
              "flex-1 pr-0",
              // The donor's `16 + depth * 16`, and its weights: a root or a
              // branch reads as a heading, a leaf as an entry.
              node.depth === 0 || hasChildren ? "font-medium" : "font-normal",
            )}
            style={{ paddingLeft: 16 + node.depth * 16 }}
          >
            {node.name}
          </MenuRowButton>
          {hasChildren ? (
            // The donor's is an `IconButton` (`CategoriesFieldPaginated.tsx:15,166-172`),
            // so it centres its ripple: a 41px disc inscribed in the 40px box,
            // at `-0.5px`. This built its own before and inherited the tree's
            // uncentred one, which measured 89.9×89.9 at `-36.93px` — a wash
            // across the row rather than a bloom in the circle. It also carried
            // `overflow-hidden`, which on a round control clips the glyph's hit
            // area as well as its paint; `IconButton` clips on the ripple layer
            // instead, as MUI does.
            // **The donor's `ListItemIcon sx={{ pl: 2 }}`, and the 16px is
            // part of the toggle rather than of the row.** The `onClick` that
            // expands the branch is on the `ListItemIcon`, not on the
            // `IconButton` inside it, and it stops propagation so the row's
            // own handler never runs
            // (`CategoriesFieldPaginated.tsx:139-142,164`). A press landing
            // between the label and the arrow therefore opens the branch and
            // chooses nothing — which is why the gap is padding here and not
            // a margin on the button: a margin would hand those 16px back to
            // the row.
            //
            // The wrapper takes a click and no key handler on purpose: the
            // interactive element is the `IconButton` inside it, which takes
            // focus and answers Enter and Space itself and whose activation
            // bubbles out through here. The wrapper only widens its hit area,
            // exactly as the donor's `ListItemIcon` does.
            <div
              className="pl-4"
              onClick={(event) => {
                event.stopPropagation();
                toggle(node.id);
              }}
            >
              <IconButton
                aria-label={`${isOpen ? "Collapse" : "Expand"} ${node.name}`}
                aria-expanded={isOpen}
              >
                {isOpen ? <MuiArrowDropDownIcon /> : <MuiArrowLeftIcon />}
              </IconButton>
            </div>
          ) : null}
        </div>
        {/* The donor slides a branch open rather than snapping it —
            `<Collapse in={isExpanded} timeout='auto' unmountOnExit>`
            (`CategoriesFieldPaginated.tsx:172-190`). This was a bare
            conditional, so the sub-list appeared at full height on the first
            frame (#630). */}
        {hasChildren ? (
          <MuiCollapse in={isOpen}>
            <ul className="list-none p-0">
              {node.children.map((child) => rows(child, walkable && isOpen))}
            </ul>
          </MuiCollapse>
        ) : null}
      </li>
    );
  };

  return (
    <div className={cn("group relative w-full", className)} data-testid={testId}>
      {/* The outlined field's label, shrunk onto the outline — the position
          MUI gives it when the field is never empty-looking, which the donor
          forces here with `shrink={... || allowEmpty}` and this control earns
          anyway by always showing a value ("All Categories" when nothing is
          picked). The white background is what makes the outline appear
          notched; the tint while open is MUI's focused label. */}
      <span
        // The same state marker `OutlinedField` stamps, so "is this label up or
        // in the box" is one readable fact across every ported field.
        data-shrink={shrunk}
        className={cn(
          shrunk
            ? MUI_FIELD_LABEL
            : // MUI's resting offset is the field's, not one number:
              // `translate(14px, 16px)` at 56px, `translate(14px, 9px)` at 40
              // (`InputLabel.js:146-164`). Same condition as the trigger's own
              // height below, because it is the same fact.
              size === "medium"
              ? MUI_FIELD_LABEL_RESTING
              : MUI_FIELD_LABEL_RESTING_SMALL,
          MUI_FIELD_LABEL_TRANSITION,
          MUI_FIELD_LABEL_PRESS_STAND_DOWN,
          focused && !error && MUI_FIELD_LABEL_FOCUSED,
          error && "text-lennar-error",
          // MUI turns a disabled `FormLabel` to `text.disabled`
          // (`FormLabel.js:67-68`), and the donor's `FormControl` here is
          // disabled while the tree is in flight as well as during a save
          // (`CategoriesFieldPaginated.tsx:253`) — so this is the state the
          // field wears on every page load, not an edge case. The trigger
          // already greys; the label was staying `text.secondary` beside it.
          (loading || disabled) && "text-lennar-ink-disabled",
        )}
      >
        {label}
        {required ? <RequiredAsterisk /> : null}
      </span>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) setOpen(true);
          else close();
        }}
      >
        {/* MUI's `.MuiOutlinedInput-root`: a positioned box the size of the
            control, holding it and the fieldset that draws its line. */}
        <div className="relative">
          <PopoverTrigger
            // MUI's `Select` renders its trigger as a combobox, which is what
            // makes "the thing that opens the listbox" nameable as one control to
            // a screen reader rather than as an unrelated button.
            role="combobox"
            aria-label={label}
            aria-haspopup="listbox"
            // The donor's `FormControl` carries
            // `disabled={disabled || isFetching || isSubmitting}`: a field that
            // opens onto a tree which has not arrived is a menu with one row
            // reading "Loading".
            disabled={loading || disabled}
            onFocus={() => setTriggerFocused(true)}
            onBlur={() => setTriggerFocused(false)}
            className={cn(
              size === "medium" ? MUI_FIELD_NOTCHED_TRIGGER_MEDIUM : MUI_FIELD_NOTCHED_TRIGGER,
              // **5px, one more than every other field on the form.** The donor
              // puts `borderRadius: '5px'` in this `Select`'s own `sx`
              // (`CategoriesFieldPaginated.tsx:274`), which lands on the
              // `OutlinedInput` root, and MUI's `NotchedOutline` fieldset is
              // `borderRadius: 'inherit'` — so the visible outline is 5px where
              // its neighbours fall through to `shape.borderRadius`, 4. The
              // donor's own inconsistency, and it is on this component rather
              // than on the edit page because the filter sidebar draws the same
              // component and carries the same `sx`.
              "rounded-[5px]",
              "flex w-full items-center",
              // The caret is absolutely placed against this trigger now, so it
              // is the containing block and owes the 32px `.MuiSelect-select`
              // reserves. With nothing in the row to push apart, `justify-between`
              // and the `gap-2` would only stretch the value's box.
              // Why each of the three: the JSDoc on each in `mui-select-caret.tsx`.
              MUI_SELECT_CARET_ANCHOR,
              MUI_SELECT_CARET_DISABLED,
              MUI_SELECT_CARET_GUTTER,
            )}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? messageId : undefined}
          >
            {/* A selected id the tree cannot name reads as `#999` rather than as
              "All Categories": the read may be in flight, may have failed onto
              a stale cache, or the category may be soft-deleted while the URL
              deliberately names it — and a filter the trigger denies is
              applying is a list nobody can explain. */}
            <span className="truncate">
              {selectedName ??
                unresolvedLabel ??
                (value === null ? (allowEmpty ? emptyLabel : "") : `#${value}`)}
            </span>
            <MuiSelectCaret open={open} />
          </PopoverTrigger>
          {/* **The gap follows MUI's `notched`, which is not its `shrink`.** The
              donor's `Select` passes no `notched`, so `OutlinedInput` computes
              `Boolean(startAdornment || filled || focused)`
              (`OutlinedInput.js:238`) — while the `InputLabel` beside it takes the
              *explicit* `shrink` above. The two genuinely disagree in the source:
              focus an empty required Category and the border opens under a label
              that is still sitting inside the box. `allowEmpty` is the third term
              because `Select` forces `notched: true` whenever `displayEmpty` is on
              (`Select.js:142-143`), which is how the sidebar's filter keeps its gap
              cut with nothing picked — measured on the running donor's items list,
              its Category legend is `max-width: 100%` at rest and unfocused.
              `searching` and `loading` are deliberately *not* here: they shrink the
              donor's label and leave its `notched` alone. */}
          {/* After the trigger, so it reads the trigger's state as its `peer`
              (`field-notch.tsx`), at the trigger's 5px radius. */}
          <FieldNotch
            label={label}
            required={required}
            notched={allowEmpty || value !== null || focused}
            className={cn(MUI_FIELD_OUTLINE_PEER, "rounded-[5px]")}
          />
        </div>
        {/* Wider than the trigger where the rows need it — MUI sizes a
            `Select`'s menu to its content from the trigger's width up, and in a
            250px sidebar the deep category names depend on it. */}
        {/* Flush against the field — `CategoriesFieldPaginated` passes no
            `MenuProps`, so its menu takes MUI's `Select` default: `anchorOrigin
            {bottom, center}` / `transformOrigin {top, center}`, no offset
            (`SelectInput.js:772-779`). */}
        <MenuPopoverContent align="center" sideOffset={0} className={MUI_SELECT_MENU_PANEL}>
          {/* MUI's `MenuList`: the list's 8px lives here, inside the scroll
              area, so the search box below pins to the panel's own top edge
              instead of 8px under it — see `MUI_MENU_SURFACE`. */}
          {/* **The keys are caught here, not on the search input.** They were
              on the input alone, so an ordinary mouse click on a branch's
              expander — which takes focus, as any button does — left the
              arrow keys dead until the user clicked back into the search box
              (#630). The donor has no such hole: its `Menu` handles keys for
              the whole surface, so focus sitting on an `IconButton` costs
              nothing. Bound on the container, a key from the input still
              arrives by bubbling, so the search box behaves exactly as it
              did. */}
          <div role="none" className={MUI_MENU_LIST} onKeyDown={onMenuKeyDown}>
            {/* `sx={{ p: 1.6 }}` on the donor's sticky `TextField` root —
                12.8px, the same box the lookup menus' search sits in
                (`search-input.tsx:35-42`). */}
            <div className="sticky top-0 z-10 bg-white p-[12.8px]">
              <input
                // **Focus stays here on open — a named parity break, owner-ruled
                // on #576 (item E).** The same box and the same ruling as the
                // lookup menus' search; the full record is on `MenuSelectSearch`'s
                // `autoFocus` (`menu-select.tsx`). Pinned in the browser test
                // ("typeable at once").
                autoFocus
                // `type="text"`, as the donor's bare `TextField` is
                // (`search-input.tsx:27-43`). `type="search"` had Chrome and
                // Safari drawing their own clear "×" inside the field, which
                // the source has nowhere.
                type="text"
                value={searchTerm}
                placeholder={searchPlaceholder}
                onChange={(event) => {
                  onSearchTermChange(event.target.value);
                  setActiveIndex(-1);
                }}
                // The arrows walk the rows while focus stays in the search field,
                // which is where MUI's own `Select` leaves it: the highlighted row
                // is named by `aria-activedescendant` rather than focused. The
                // handler itself is on the menu container above — bound here as
                // well it would run twice per key and step two rows at a time.
                aria-controls={listId}
                aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                className={cn(MUI_FIELD_TRIGGER, "w-full")}
              />
            </div>
            <ul
              ref={listRef}
              role="listbox"
              id={listId}
              aria-label={label}
              className="m-0 list-none p-0"
            >
              {allowEmpty ? (
                <>
                  <li
                    role="option"
                    id={`${listId}-0`}
                    data-row-index={0}
                    aria-selected={value === null}
                    // `sx={{ mt: 1 }}` on the donor's empty row
                    // (`CategoriesFieldPaginated.tsx:303`) — without it the row
                    // sits hard under the search box and the whole list below
                    // it rides 8px high.
                    // Its tint runs through the same matrix; it used to be split —
                    // the selected navy on the child button, hover on the
                    // `<li>` — which is what let the button's
                    // `hover:bg-transparent` erase the selection.
                    className={cn("mt-2", rowTint(value === null, activeIndex === 0))}
                  >
                    <MenuRowButton onClick={() => pick(null)} className="font-medium">
                      {emptyLabel}
                    </MenuRowButton>
                  </li>
                  <li role="presentation" className="bg-lennar-divider my-2 h-px" />
                </>
              ) : null}
              {/* Nothing is said about a failed read: the donor's category menu
                  is the search box, the empty row and the tree, and it has no
                  copy of any kind for a list that did not arrive
                  (`CategoriesFieldPaginated.tsx:298-313`). */}
              {nodes.map((node) => rows(node))}
            </ul>
          </div>
        </MenuPopoverContent>
      </Popover>
      {/* MUI's `FormHelperText` under an errored `FormControl`: 12px, the error
          colour, and inset to the field's own side padding. */}
      {error ? (
        <p id={messageId} className={cn("text-lennar-error", MUI_FIELD_HELPER_TEXT)}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
